import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Camera, X, CheckCircle2, Receipt, AlertTriangle, DollarSign, Images,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { cachePhoto, queueItem } from "@/lib/offline";   // T-401
import { shrinkImage } from "@/lib/shrinkImage";         // 8 Oct 2026 — photos shrunk before upload
import { useRequireAuth } from "@/lib/auth";
import { Button, Card, Field, Input, Select, Textarea } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";

// =============================================================================
// MobileReceiptScreen — /receipts/new
// Deliberately bare: one big photo button, three optional fields, submit.
// Field crew snap + go; director codes the rest later in the existing
// ApCaptureScreen. T-033 Commit A.
// =============================================================================

const BUCKET             = "receipt-captures";
const MAX_PHOTO_BYTES    = 10 * 1024 * 1024;
const ACCEPTED_MIME      = ["image/jpeg", "image/png", "image/webp", "image/heic", "application/pdf"];

export default function MobileReceiptScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);   // T-445 — gallery / PDF picker (no forced camera)

  // Form state
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [supplierGuess, setSupplierGuess] = useState("");
  const [amountGuess, setAmountGuess]     = useState("");
  const [projectId, setProjectId]         = useState("");
  const [notes, setNotes]                 = useState("");
  // T-374b v2 — MANDATORY, no default (James: field guys won't scroll and will
  // rubber-stamp any default). null = not answered = can't submit.
  const [claimedPersonal, setClaimedPersonal] = useState<boolean | null>(null);
  const [errMsg, setErrMsg]               = useState<string | null>(null);

  // Project dropdown — live projects only (optional)
  const { data: projects = [] } = useQuery({
    queryKey: ["receipts_project_options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name")
        .eq("status", "live")
        .order("project_code");
      if (error) throw error;
      return data ?? [];
    },
  });

  // Preview URL lifecycle
  useEffect(() => {
    if (!file) { setPreview(null); return; }
    if (file.type === "application/pdf") { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    if (!ACCEPTED_MIME.includes(picked.type)) {
      setErrMsg("Unsupported file. JPEG / PNG / WEBP / HEIC / PDF only.");
      return;
    }
    // 8 Oct 2026 — shrink the photo HERE, before the size check and the upload.
    // A modern phone shoots 8–12 MB; the AI refuses anything over 10 MB once
    // base64-encoded (~7.5 MB on disk). 2200 px on the long side reads fine.
    const f = await shrinkImage(picked);
    if (f.size > MAX_PHOTO_BYTES) {
      setErrMsg(`File too big (${(f.size / 1024 / 1024).toFixed(1)} MB, max 10 MB).`);
      return;
    }
    setErrMsg(null);
    setFile(f);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  };

  // T-401 — the old flow DELETED the uploaded photo when the row insert
  // failed, so a receipt snapped in a dead zone was simply LOST. Now: any
  // failure (or being offline from the start) caches the photo + details in
  // IndexedDB and the sync loop uploads it when signal returns. receipt_id
  // is minted client-side so retries can never double-insert.
  const submitMut = useMutation({
    mutationFn: async (): Promise<{ queued: boolean }> => {
      if (!file) throw new Error("Please take a photo or attach a file.");
      if (claimedPersonal === null) throw new Error("Pick how you paid — company card or your own money.");
      if (!user?.employee_id) throw new Error("Your login isn't linked to an employee.");

      const ext        = (file.name.split(".").pop() || "jpg").toLowerCase();
      const stamp      = new Date().toISOString().replace(/[:.]/g, "-");
      const rand       = Math.random().toString(36).slice(2, 8);
      const path       = `${user.employee_id}/${stamp}-${rand}.${ext}`;
      const receipt_id = crypto.randomUUID();
      const row = {
        captured_by_employee_id: user.employee_id,
        supplier_guess: supplierGuess.trim() || null,
        amount_guess:   amountGuess ? parseFloat(amountGuess) : null,
        project_id:     projectId || null,
        notes:          notes.trim() || null,
        claimed_personal: claimedPersonal,   // T-374b — worker's own declaration
      };

      const queueForLater = async () => {
        await cachePhoto(`receipt_${receipt_id}`, file, { file_name: file.name });
        await queueItem("receipt_capture", {
          local_id:     `receipt_${receipt_id}`,
          receipt_id,
          storage_path: path,
          mime_type:    file.type,
          size_bytes:   file.size,
          row,
        });
      };

      if (!navigator.onLine) {
        await queueForLater();
        return { queued: true };
      }

      try {
        const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
          contentType: file.type,
          cacheControl: "3600",
          upsert: false,
        });
        if (upErr) throw upErr;

        const { error: rowErr } = await supabase
          .from("pending_receipts")
          .insert({ receipt_id, storage_path: path, mime_type: file.type, size_bytes: file.size, ...row });
        if (rowErr) throw rowErr;

        // T-086 Phase B — fire-and-forget AI extraction; manager can re-run.
        // 8 Oct 2026 — keepalive so the browser finishes the call even when the
        // phone goes in a pocket straight after Submit; and the worker's
        // 15-minute sweep (mig 424) picks up anything that still never landed.
        apiFetch("/ai/extract-document", {
          method: "POST",
          keepalive: true,
          body: JSON.stringify({ pending_receipt_id: receipt_id }),
        }).catch((e) => console.warn("[receipt] AI extraction failed (non-blocking)", e));

        return { queued: false };
      } catch (err) {
        // Flaky signal mid-upload, RLS hiccup, anything — the photo is NEVER
        // lost. Queue it; the replay is idempotent (dup upload/row tolerated).
        console.warn("[receipt] direct submit failed — queued for sync", err);
        await queueForLater();
        return { queued: true };
      }
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["my_receipts"] });
      if (res.queued) {
        alert("No signal (or upload hiccup) — your receipt is saved on this phone and will upload itself when you're back in coverage. Nothing else to do.");
      }
      navigate("/receipts");
    },
    onError: (e: unknown) => setErrMsg(e instanceof Error ? e.message : "Submit failed"),
  });

  // T-374b v2 — no submit until the payment question is answered.
  const canSubmit = !!file && claimedPersonal !== null && !submitMut.isPending;

  return (
    <div className="p-4 md:p-6 max-w-lg mx-auto pb-24">
      <button
        onClick={() => navigate("/receipts")}
        className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
        type="button"
      >
        <ArrowLeft size={14} /> My receipts
      </button>

      <div className="mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-gradient-to-br from-accent/20 to-accent/5 text-accent flex items-center justify-center border border-accent/30">
            <Receipt size={22} />
          </div>
          <div>
            <h1 className="text-[22px] font-semibold text-text">Scan a receipt</h1>
            <p className="text-[13px] text-text-muted mt-0.5">
              Snap it, submit it — a director codes it later.
            </p>
          </div>
        </div>
      </div>

      {/* Big primary photo capture */}
      <Card>
        <div className="p-5">
          {/* Camera path — the fast "snap + go" the crew already know */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_MIME.join(",")}
            capture="environment"
            onChange={onFilePicked}
            className="hidden"
          />
          {/* T-445 — gallery / files path: NO capture attribute, so mobile
              offers Photo Library + Choose File (incl. supplier PDFs) instead
              of forcing the camera. */}
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={onFilePicked}
            className="hidden"
          />
          {file ? (
            <div>
              {preview ? (
                <div className="relative">
                  <img src={preview} alt="Receipt" className="w-full max-h-[50vh] object-contain rounded-md border border-line bg-ink" />
                  <button
                    type="button"
                    onClick={clearFile}
                    className="absolute top-2 right-2 p-1.5 bg-black/70 hover:bg-black/90 text-white rounded-full"
                    aria-label="Remove photo"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="p-6 border border-line rounded-md bg-ink-raised flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded bg-accent/15 text-accent flex items-center justify-center">
                      <Receipt size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] text-text truncate">{file.name}</div>
                      <div className="text-[11px] text-text-muted font-mono">
                        {(file.size / 1024).toFixed(0)} KB · {file.type}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearFile}
                    className="p-2 text-text-muted hover:text-text"
                    aria-label="Remove"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
              <div className="text-[11px] text-text-muted mt-2 text-center">
                Tap the file to replace, or the ✕ to clear.
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full aspect-[4/3] border-2 border-dashed border-line rounded-md flex flex-col items-center justify-center gap-3 hover:border-accent hover:bg-accent/5 transition-colors text-text-muted"
              >
                <div className="w-16 h-16 rounded-full bg-accent/12 text-accent flex items-center justify-center">
                  <Camera size={28} />
                </div>
                <div className="text-[14px] font-medium text-text">Take photo of receipt</div>
                <div className="text-[11px]">JPG / PNG / WEBP / HEIC / PDF · up to 10 MB</div>
              </button>
              {/* T-445 — crew often already have the receipt in their gallery,
                  or the supplier emailed a PDF; camera-only made them re-snap. */}
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-md border border-line text-[13px] text-text-muted hover:border-accent hover:text-accent transition-colors"
              >
                <Images size={16} /> Choose from gallery or a PDF
              </button>
            </>
          )}
        </div>
      </Card>

      {/* T-374b v2 — STEP 2, mandatory, no default, no scrolling required:
          whose money was it? Big tap targets; pulses until answered. */}
      {file && (
        <Card className={`mt-4 ${claimedPersonal === null ? "border-accent/60" : claimedPersonal ? "border-warn/50" : "border-ok/40"}`}>
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold ${claimedPersonal === null ? "bg-accent text-white" : claimedPersonal ? "bg-warn text-white" : "bg-ok text-white"}`}>
                {claimedPersonal === null ? "?" : "✓"}
              </span>
              <span className="text-[14px] font-semibold text-text">How did you pay?</span>
              {claimedPersonal === null && <span className="text-[11px] text-accent ml-auto">required</span>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setClaimedPersonal(false)}
                className={`px-3 py-4 rounded-lg border-2 text-center transition-colors ${
                  claimedPersonal === false
                    ? "bg-ok/15 border-ok text-ok"
                    : "bg-ink border-line text-text-muted active:bg-ink-raised"
                }`}
              >
                <div className="text-[15px] font-semibold">Company card</div>
                <div className="text-[11px] mt-0.5 opacity-80">work paid for it</div>
              </button>
              <button
                type="button"
                onClick={() => setClaimedPersonal(true)}
                className={`px-3 py-4 rounded-lg border-2 text-center transition-colors ${
                  claimedPersonal === true
                    ? "bg-warn/15 border-warn text-warn"
                    : "bg-ink border-line text-text-muted active:bg-ink-raised"
                }`}
              >
                <div className="text-[15px] font-semibold">My own money</div>
                <div className="text-[11px] mt-0.5 opacity-80">pay me back</div>
              </button>
            </div>
            {claimedPersonal === true && (
              <div className="text-[12px] text-warn mt-2.5 text-center">
                Flagged as a reimbursement — you'll see it as <span className="font-semibold">owed to you</span> until it's paid back.
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Optional fields — only show if file is picked so the capture feels like step 1 */}
      {file && (
        <Card className="mt-4">
          <div className="p-5 space-y-4">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono flex items-center justify-between">
              <span>Optional — if you have time</span>
              <span className="text-text-dim">skip &amp; a director fills in</span>
            </div>

            <Field label="Supplier">
              <Input
                value={supplierGuess}
                onChange={e => setSupplierGuess(e.target.value)}
                placeholder="e.g. Bunnings, Sydney Tools, Officeworks"
                autoCapitalize="words"
              />
            </Field>

            <Field label="Amount (inc. GST)">
              <div className="relative">
                <DollarSign size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                <Input
                  type="number"
                  inputMode="decimal"
                  mono
                  step="0.01"
                  value={amountGuess}
                  onChange={e => setAmountGuess(e.target.value)}
                  placeholder="0.00"
                  className="pl-7"
                />
              </div>
            </Field>

            <Field label="Project (if it's for a job)">
              <Select value={projectId} onChange={e => setProjectId(e.target.value)}>
                <option value="">— not job-specific —</option>
                {(projects as any[]).map(p => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.project_code} · {p.project_name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Notes">
              <Textarea
                rows={2}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Anything the director should know."
              />
            </Field>
          </div>
        </Card>
      )}

      {/* Sticky save bar — mobile pattern matching the rigging inspection form */}
      <StickyBottomBar>
        {errMsg && (
          <div className="px-3 py-2 bg-red-900/30 border-b border-red-900/50 text-red-200 text-[12px] text-center flex items-center justify-center gap-1.5">
            <AlertTriangle size={12} />
            {errMsg}
          </div>
        )}
        <div className="p-3 flex gap-2 max-w-lg mx-auto">
          <Button variant="secondary" size="lg" onClick={() => navigate("/receipts")} type="button">
            Cancel
          </Button>
          <Button
            size="lg"
            icon={CheckCircle2}
            onClick={() => submitMut.mutate()}
            disabled={!canSubmit}
            className="flex-1 justify-center"
            type="button"
          >
            {submitMut.isPending ? "Submitting…"
              : !file ? "Take the photo first"
              : claimedPersonal === null ? "Pick how you paid ↑"
              : "Submit receipt"}
          </Button>
        </div>
      </StickyBottomBar>
    </div>
  );
}
